import React, { useEffect, useState } from 'react';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import InputField from '../../../../../../Components/Form/components/InputField';
import Text from '../../../../../../Components/Base/Text/Text';
// @ts-ignore
import styles from '../../PatientManualRegistration.module.scss';
import Loading from '../../../../../../Components/Loading';
import Button from '../../../../../../Components/Button/Button';
import { Theme } from '../../../../../../Enums/Theme.enum';
import FormContainer from '../../../../../../Components/Form/FormContainer';
import FormControl from '../../../../../../Components/Form/FormControl';
import HealthInsuranceCompanySelect from '../../../../../Term/Components/HealthInsuranceCompanySelect/HealthInsuranceCompanySelect';
import PhoneNumberField from '../../../../../../Components/PhoneNumber/PhoneNumberField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import CheckboxField from '../../../../../../Components/Form/components/CheckboxField';
import useKDARConnection from '../../../../Hooks/useKDARConnection';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';
import TextAreaField from '../../../../../../Components/Form/components/TextAreaField';
import usePatientManualRegistrationKDARFormValidationScheme from '../../../../Validation/PatientManualRegistrationKDARFormValidationScheme';
import { yupResolver } from '@hookform/resolvers/yup';
import { isValidPhoneNumber } from 'libphonenumber-js';

export interface PatientManualRegistrationKDARFormValues {
	insuranceNumber: string;
	firstName: string;
	lastName: string;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	healthInsuranceCompany?: SelectValue;
	sendPatientInviteToMeddi: boolean;
	healthPlanCode: SelectValue;
	healthPlanWorkplaceMember?: {
		value?: string;
		id?: string;
		label: string;
		item: any;
	};
	legalRepresentative?: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber?: string;
	};
	note?: string;
}

interface Props {
	onSuccess: (values: PatientManualRegistrationKDARFormValues) => void;
}

const PatientManualRegistrationKDARForm: React.FC<Props> = ({ onSuccess }) => {
	const [isLoading, setIsLoading] = useState(false);
	const { loadDataFromNIS } = useKDARConnection();
	const { validationSchema } =
		usePatientManualRegistrationKDARFormValidationScheme();
	const [isLoadFromNISAvailable, setIsLoadFromNISAvailable] = useState(false);
	const formMethods = useForm({ resolver: yupResolver(validationSchema) });
	const insuranceNumberValue = formMethods.watch('insuranceNumber');

	const handleSuccessWithPlan = (vals: any) => {
		const values: PatientManualRegistrationKDARFormValues = vals;
		if (values.legalRepresentative?.phoneNumber) {
			if (!isValidPhoneNumber(values.legalRepresentative.phoneNumber, 'CZ')) {
				values.legalRepresentative.phoneNumber = undefined;
			}
		}
		onSuccess(values as PatientManualRegistrationKDARFormValues);
	};

	const handleLoadDefaultDataByBirthNumber = async () => {
		setIsLoading(true);
		const values = await loadDataFromNIS(insuranceNumberValue);
		if (values) {
			formMethods.reset(values);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		setIsLoadFromNISAvailable(insuranceNumberValue?.length >= 9);
	}, [insuranceNumberValue]);

	return (
		<FormWrapper formMethods={formMethods}>
			<div className='v2-forms__gray-background'>
				<div className='row'>
					<div className='col-lg-4 col-12'>
						<InputField
							name='insuranceNumber'
							label='base.shared.insuranceNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-8 col-12'>
						<Text
							tx='base.verificationOfNamePermanentResidenceAddressAndInsuranceValidity'
							className={styles.hintText}
						/>
						<Button
							isLoading={isLoading}
							title='component.patientManualRegistrationCzechForm.automaticFillUsingKDAR'
							disabled={!isLoadFromNISAvailable}
							onClick={handleLoadDefaultDataByBirthNumber}
							theme={Theme.BLUE}
						/>
					</div>
				</div>
			</div>
			<div className='row'>
				<div className='col-lg-6 col-12'>
					<InputField
						name='firstName'
						label='base.shared.firstName'
						isRequired
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<InputField name='lastName' label='base.shared.lastName' isRequired />
				</div>
			</div>
			<Text className={styles.formText} tx='base.permanentAddress' firstUpper />
			<FormContainer name={'address'}>
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField name='street' label='base.shared.street' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='descriptiveNumber'
							label='base.shared.descriptiveNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-3 col-12'>
						<InputField name='guideNumber' label='base.shared.guideNumber' />
					</div>
				</div>
				<div className='row'>
					<div className='col-lg-6 col-12'>
						<InputField name='city' label='base.shared.city' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='postCode'
							label='base.shared.postCode'
							isRequired
						/>
					</div>
				</div>
			</FormContainer>
			<Text
				className={styles.formText}
				tx='base.additionalInformations'
				firstUpper
			/>
			<FormControl
				name='healthInsuranceCompany'
				label='base.healthInsuranceCompany'>
				<HealthInsuranceCompanySelect />
			</FormControl>
			<Text
				className={styles.formText}
				tx='base.shared.legalRepresentative'
				firstUpper
			/>
			<InputWrapper>
				<FormContainer name={'legalRepresentative'}>
					<div className='row mb-3'>
						<div className='col-lg-6 col-12'>
							<InputField name='firstName' label='base.shared.firstName' />
						</div>
						<div className='col-lg-6 col-12'>
							<InputField name='lastName' label='base.shared.lastName' />
						</div>
					</div>
					<div className='row'>
						<div className='col-lg-6 col-12'>
							<PhoneNumberField name='phoneNumber' label='base.shared.phone' />
						</div>
						<div className='col-lg-6 col-12'>
							<InputField name='email' label='base.shared.email' />
						</div>
					</div>
				</FormContainer>
			</InputWrapper>
			<InputWrapper>
				<TextAreaField name='note' label='base.shared.note' />
			</InputWrapper>
			<div className='v2-forms__gray-background footer mt-3'>
				<div
					className='d-flex flex-row align-items-center'
					style={{ gap: '10px' }}>
					<div>
						<SubmitButton
							disabled={isLoading}
							onSubmit={handleSuccessWithPlan}
							title='component.patientManualRegistrationComponent.registerAndSetupHealthPlan'
						/>
					</div>
				</div>
			</div>
		</FormWrapper>
	);
};

export default PatientManualRegistrationKDARForm;
